import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { localizeHref } from '@/lib/marketing/localizedLandings';
import {
  Menu,
  X,
  ChevronDown,
  Stethoscope,
  Building2,
  Sparkles,
  Scissors,
  Brush,
  Dumbbell,
  Target,
  CircleDot,
  HeartHandshake,
  Users,
} from 'lucide-react';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useAuth } from '@/contexts/auth-context';

const SOLUTIONS = [
  {
    labelKey: 'home:solutions.healthcare.label',
    items: [
      { labelKey: 'home:solutions.healthcare.items.doctors', href: '/for-doctors', Icon: Stethoscope },
      { labelKey: 'home:solutions.healthcare.items.clinics', href: '/for-clinics', Icon: Building2 },
      { labelKey: 'home:solutions.healthcare.items.spa', href: '/for-spa', Icon: Sparkles },
      { labelKey: 'home:solutions.healthcare.items.therapists', href: '/for-therapists', Icon: HeartHandshake },
    ],
  },
  {
    labelKey: 'home:solutions.beauty.label',
    items: [
      { labelKey: 'home:solutions.beauty.items.salons', href: '/for-salons', Icon: Scissors },
      { labelKey: 'home:solutions.beauty.items.barbers', href: '/for-barbers', Icon: Scissors },
      { labelKey: 'home:solutions.beauty.items.beautyPros', href: '/for-beauty', Icon: Brush },
    ],
  },
  {
    labelKey: 'home:solutions.fitness.label',
    items: [
      { labelKey: 'home:solutions.fitness.items.trainers', href: '/for-trainers', Icon: Dumbbell },
      { labelKey: 'home:solutions.fitness.items.smallGyms', href: '/for-small-gyms', Icon: Users },
      { labelKey: 'home:solutions.fitness.items.coaches', href: '/for-coaches', Icon: Target },
      { labelKey: 'home:solutions.fitness.items.golfTeachers', href: '/for-golf-teachers', Icon: CircleDot },
    ],
  },
];

interface MarketingNavProps {
  /** Render as a fixed top bar (white background) so the page can offset its content. */
  fixed?: boolean;
}

/**
 * Shared marketing navbar used across landing/marketing pages and the
 * /register flow so the public-facing identity is consistent.
 */
export function MarketingNav({ fixed = false }: MarketingNavProps) {
  const { t, i18n } = useTranslation(['header', 'common', 'home']);
  const { isAuthenticated, user } = useAuth();
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setSolutionsOpen(true);
  };
  const scheduleClose = () => {
    closeTimeout.current = setTimeout(() => setSolutionsOpen(false), 150);
  };
  useEffect(() => () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
  }, []);

  const dashboardUrl = user?.role === 'staff' ? '/staff/dashboard' : '/admin/dashboard';

  return (
    <div className={`mn-shell ${fixed ? 'mn-fixed' : ''}`}>
      <style>{styles}</style>

      <nav className="mn-nav">
        <Link href="/" className="brand">
          <img src="/images/logo_horizontal_transparent.png" alt="Sloty" className="brand-logo" />
        </Link>

        <div className="links desktop-only">
          <Link href="/features">{t('header:features')}</Link>
          <div className="solutions" onMouseEnter={open} onMouseLeave={scheduleClose}>
            <button className="solutions-trigger" type="button">
              {t('home:nav.solutions')} <ChevronDown className="chev" />
            </button>
            {solutionsOpen && (
              <div className="solutions-panel" onMouseEnter={open} onMouseLeave={scheduleClose}>
                <div className="sol-grid">
                  {SOLUTIONS.map((cat) => (
                    <div key={cat.labelKey} className="sol-col">
                      <div className="sol-h">{t(cat.labelKey)}</div>
                      <ul>
                        {cat.items.map(({ labelKey, href, Icon }) => (
                          <li key={href}>
                            <Link href={localizeHref(href, i18n.language)}>
                              <Icon className="sol-ic" /> {t(labelKey)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link href="/pricing">{t('header:pricing')}</Link>
          <Link href="/about">{t('header:about')}</Link>
          <Link href="/blog">{t('header:blog')}</Link>

          <LanguageSelector />

          {isAuthenticated ? (
            <Link href={dashboardUrl} className="cta">{t('header:dashboard')} →</Link>
          ) : (
            <>
              <Link href="/login">{t('header:login')}</Link>
              <Link href="/register" className="cta">{t('header:register')} →</Link>
            </>
          )}
        </div>

        <div className="mobile-only mobile-actions">
          <LanguageSelector />
          <button
            className="mobile-toggle"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            type="button"
          >
            {mobileMenuOpen ? <X className="ic" /> : <Menu className="ic" />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="mobile-menu mobile-only">
          <Link href="/features" onClick={() => setMobileMenuOpen(false)}>{t('header:features')}</Link>
          <button
            type="button"
            className="m-solutions-trigger"
            onClick={() => setMobileSolutionsOpen((v) => !v)}
          >
            {t('home:nav.solutions')} <ChevronDown className={`chev ${mobileSolutionsOpen ? 'open' : ''}`} />
          </button>
          {mobileSolutionsOpen && (
            <div className="m-solutions">
              {SOLUTIONS.map((cat) => (
                <div key={cat.labelKey}>
                  <div className="sol-h">{t(cat.labelKey)}</div>
                  {cat.items.map(({ labelKey, href }) => (
                    <Link key={href} href={localizeHref(href, i18n.language)} onClick={() => setMobileMenuOpen(false)}>
                      {t(labelKey)}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}
          <Link href="/pricing" onClick={() => setMobileMenuOpen(false)}>{t('header:pricing')}</Link>
          <Link href="/about" onClick={() => setMobileMenuOpen(false)}>{t('header:about')}</Link>
          <Link href="/blog" onClick={() => setMobileMenuOpen(false)}>{t('header:blog')}</Link>
          {isAuthenticated ? (
            <Link href={dashboardUrl} onClick={() => setMobileMenuOpen(false)} className="cta">{t('header:dashboard')} →</Link>
          ) : (
            <>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)}>{t('header:login')}</Link>
              <Link href="/register" onClick={() => setMobileMenuOpen(false)} className="cta">{t('header:register')} →</Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const styles = `
.mn-shell{
  --mn-foreground: 215 25% 27%;
  --mn-muted-foreground: 215 16% 35%;
  --mn-border: 220 13% 91%;
  --mn-action-primary: 217 91% 53%;
  --mn-action-primary-hover: 224 76% 42%;
  --mn-action-primary-light: 217 91% 95%;
  --mn-brand-500:#2563EB;
  --mn-brand-700:#1E40AF;
  font-family:Inter,system-ui,sans-serif;
  -webkit-font-smoothing:antialiased;
  color:hsl(var(--mn-foreground));
}
.mn-shell.mn-fixed{position:fixed;top:0;left:0;right:0;z-index:50;background:#fff;border-bottom:1px solid hsl(var(--mn-border));}
.mn-shell *{box-sizing:border-box;}
.mn-shell .mn-nav{display:flex;justify-content:space-between;align-items:center;padding:14px 32px;max-width:1320px;margin:0 auto;width:100%;position:relative;}
.mn-shell:not(.mn-fixed) .mn-nav{padding:22px 32px;}
.mn-shell .mn-nav a,.mn-shell .mobile-menu a{color:inherit;text-decoration:none;cursor:pointer;}
.mn-shell .brand{font-family:Lexend;font-weight:700;font-size:22px;letter-spacing:-0.02em;display:inline-flex;align-items:center;gap:10px;color:hsl(var(--mn-foreground));}
.mn-shell .brand-logo{height:40px;width:auto;display:block;}
.mn-shell .mn-nav .links{display:flex;gap:24px;align-items:center;font-size:14px;color:hsl(var(--mn-muted-foreground));}
.mn-shell .mn-nav .links a:not(.cta):hover,.mn-shell .mn-nav .solutions-trigger:hover{color:hsl(var(--mn-foreground));}
.mn-shell .mn-nav .cta{background:hsl(var(--mn-action-primary));color:#fff;padding:10px 18px;border-radius:9999px;font-size:14px;font-weight:500;min-height:44px;display:inline-flex;align-items:center;}
.mn-shell .mn-nav .cta:hover{background:hsl(var(--mn-action-primary-hover));color:#fff;}
.mn-shell .solutions{position:relative;}
.mn-shell .solutions-trigger{background:none;border:0;padding:0;font:inherit;color:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:4px;font-size:14px;}
.mn-shell .solutions-trigger .chev{width:14px;height:14px;}
.mn-shell .solutions-panel{position:absolute;top:100%;left:50%;transform:translateX(-50%);padding-top:10px;min-width:560px;z-index:60;}
.mn-shell .solutions-panel > .sol-grid{background:#fff;border:1px solid hsl(var(--mn-border));border-radius:14px;padding:20px;display:grid;grid-template-columns:repeat(3,1fr);gap:24px;box-shadow:0 14px 40px rgba(15,23,42,0.10);}
.mn-shell .solutions-panel .sol-h{font-size:11px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--mn-muted-foreground));margin-bottom:10px;}
.mn-shell .solutions-panel ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:4px;}
.mn-shell .solutions-panel li a{display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:8px;font-size:14px;color:hsl(var(--mn-foreground));}
.mn-shell .solutions-panel li a:hover{background:hsl(var(--mn-action-primary-light));color:var(--mn-brand-700);}
.mn-shell .sol-ic{width:16px;height:16px;color:var(--mn-brand-500);}
.mn-shell .mobile-actions{display:none;align-items:center;gap:8px;}
.mn-shell .mobile-toggle{background:none;border:0;padding:8px;cursor:pointer;color:hsl(var(--mn-foreground));}
.mn-shell .mobile-toggle .ic{width:24px;height:24px;}
.mn-shell .mobile-only{display:none;}
.mn-shell .desktop-only{display:flex;}
.mn-shell .mobile-menu{flex-direction:column;gap:4px;padding:12px 24px 20px;border-bottom:1px solid hsl(var(--mn-border));background:#fff;}
.mn-shell .mobile-menu a,.mn-shell .mobile-menu .m-solutions-trigger{padding:12px 8px;font-size:15px;color:hsl(var(--mn-foreground));border-radius:8px;background:none;border:0;text-align:left;font-family:inherit;display:flex;align-items:center;justify-content:space-between;cursor:pointer;}
.mn-shell .mobile-menu .cta{background:hsl(var(--mn-action-primary));color:#fff;justify-content:center;margin-top:6px;}
.mn-shell .mobile-menu .cta:hover{background:hsl(var(--mn-action-primary-hover));color:#fff;}
.mn-shell .mobile-menu .m-solutions-trigger .chev{width:16px;height:16px;transition:transform .2s;}
.mn-shell .mobile-menu .m-solutions-trigger .chev.open{transform:rotate(180deg);}
.mn-shell .m-solutions{display:flex;flex-direction:column;gap:14px;padding:6px 0 6px 12px;border-left:2px solid hsl(var(--mn-border));margin-left:8px;}
.mn-shell .m-solutions .sol-h{font-size:11px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--mn-muted-foreground));margin-bottom:4px;}
.mn-shell .m-solutions a{padding:8px;font-size:14px;}

@media (max-width:980px){
  /* .mn-nav prefix: must out-rank ".mn-shell .mn-nav .links{display:flex}", or the
     desktop link row stays visible on phones and overflows the page sideways. */
  .mn-shell .mn-nav .desktop-only,.mn-shell .desktop-only{display:none;}
  .mn-shell .mobile-only{display:flex;}
  .mn-shell .mobile-actions{display:flex;}
}
`;
